import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./variables.css";
import "./NewSave.css";
import API_URL from "./config";

const NewSave = () => {
  const [teams, setTeams] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("teamName");
  const [characterError, setCharacterError] = useState("");
  const navigate = useNavigate();

  const SAVE_NAME_LIMIT = { min: 1, max: 30 };

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams`);
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || "Failed to load teams");
        return;
      }
      const data = await res.json();
      const sortedTeams = (data.teams || []).sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setTeams(sortedTeams);
    } catch (err) {
      setError(err.message || "Server error");
    }
  };

  const getSortedTeams = () => {
    return [...teams].sort((a, b) => {
      let valueA, valueB;

      if (sortBy === "teamName") {
        valueA = (a.name || "").toLowerCase();
        valueB = (b.name || "").toLowerCase();
      } else if (sortBy === "city") {
        valueA = (a.city || "").toLowerCase();
        valueB = (b.city || "").toLowerCase();
      }

      return valueA.localeCompare(valueB);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCharacterError("");

    if (!saveName.trim()) {
      setError("Please enter a save name");
      return;
    }

    if (saveName.length > SAVE_NAME_LIMIT.max) {
      setCharacterError(`Save name cannot exceed ${SAVE_NAME_LIMIT.max} characters`);
      return;
    }

    if (!selectedTeam) {
      setError("Please select a team");
      return;
    }

    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      if (!userId) {
        setError("Please log in to create a save");
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/saves`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": userId,
        },
        body: JSON.stringify({
          name: saveName,
          teamId: selectedTeam,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || "Failed to create save");
        setLoading(false);
        return;
      }

      const data = await res.json();
      const newSaveId = data.save._id;

      // Store active save in localStorage
      localStorage.setItem("activeSaveId", newSaveId);

      // Navigate to my-team page
      navigate("/my-team");
    } catch (err) {
      setError(err.message || "Server error");
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="newsave-wrapper">
        <div className="newsave-container">
          <Link to="/saves" className="newsave-back-link">
            ← Back to Saves
          </Link>

          <h2>Create New Save</h2>

          {error && <p className="newsave-error">{error}</p>}

          <form onSubmit={handleSubmit} className="newsave-form">
            <div className="newsave-form-group">
              <label htmlFor="saveName" className="newsave-label">
                Save Name
              </label>
              <input
                id="saveName"
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="My Season 2025"
                className="newsave-input"
                maxLength={SAVE_NAME_LIMIT.max}
                required
              />
              {characterError && <p className="character-error">{characterError}</p>}
            </div>

            <div className="newsave-form-group">
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                <label className="newsave-label" style={{ margin: 0 }}>Select Your Team</label>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={() => setSortBy("teamName")}
                    title="Sort teams by name"
                    style={{
                      padding: "0.4rem 0.8rem",
                      fontSize: "0.9rem",
                      border: "1px solid var(--color-border)",
                      backgroundColor: sortBy === "teamName" ? "var(--color-primary)" : "transparent",
                      color: sortBy === "teamName" ? "white" : "inherit",
                      borderRadius: "4px",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    Name
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortBy("city")}
                    title="Sort teams by city"
                    style={{
                      padding: "0.4rem 0.8rem",
                      fontSize: "0.9rem",
                      border: "1px solid var(--color-border)",
                      backgroundColor: sortBy === "city" ? "var(--color-primary)" : "transparent",
                      color: sortBy === "city" ? "white" : "inherit",
                      borderRadius: "4px",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                  >
                    City
                  </button>
                </div>
              </div>
              <div className="newsave-team-grid">
                {getSortedTeams().filter(t => t.name !== "Free Agent").map((t) => (
                  <div
                    key={t._id}
                    className={`newsave-team-card ${selectedTeam === t._id ? "selected" : ""}`}
                    onClick={() => setSelectedTeam(t._id)}
                  >
                    <div className="newsave-team-name">{t.name}</div>
                    {t.city && <div className="newsave-team-city">{t.city}</div>}
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="newsave-submit-btn"
            >
              {loading ? "Creating..." : "Create Save"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default NewSave;
